import { useEffect, useRef, useState } from "react";
import { X, Loader2, Camera, Zap, ZapOff } from "lucide-react";
import { useBarcodeScanner } from "@/lib/barcode-scanner-store";
import {
  useDialogFocusTrap,
  useGlobalLowerWorkspaceDialogContainment,
} from "@/lib/lower-workspace-dialog";
import type { BrowserMultiFormatReader as BrowserMultiFormatReaderType, IScannerControls } from "@zxing/browser";

interface MediaTrackCapabilitiesWithTorch extends MediaTrackCapabilities {
  torch?: boolean;
}

interface MediaTrackConstraintSetWithTorch extends MediaTrackConstraintSet {
  torch?: boolean;
}

export function BarcodeScannerModal() {
  const { isOpen, closeScanner, resolve } = useBarcodeScanner();
  const videoRef = useRef<HTMLVideoElement>(null);
  const readerRef = useRef<BrowserMultiFormatReaderType | null>(null);
  const controlsRef = useRef<IScannerControls | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [torchOn, setTorchOn] = useState(false);
  const [torchAvailable, setTorchAvailable] = useState(false);
  const streamRef = useRef<MediaStream | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const containment = useGlobalLowerWorkspaceDialogContainment();
  const focusTrap = useDialogFocusTrap(panelRef, closeScanner, isOpen);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    setError(null);
    setTorchOn(false);
    setTorchAvailable(false);

    const startScanning = async () => {
      try {
        const { BrowserMultiFormatReader } = await import("@zxing/browser");
        const { DecodeHintType, BarcodeFormat } = await import("@zxing/library");

        const hints = new Map();
        hints.set(DecodeHintType.POSSIBLE_FORMATS, [
          BarcodeFormat.EAN_13,
          BarcodeFormat.EAN_8,
          BarcodeFormat.UPC_A,
          BarcodeFormat.UPC_E,
          BarcodeFormat.CODE_128,
          BarcodeFormat.CODE_39,
          BarcodeFormat.QR_CODE,
        ]);
        hints.set(DecodeHintType.TRY_HARDER, true);

        const reader = new BrowserMultiFormatReader(hints);
        readerRef.current = reader;

        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
        });
        if (cancelled) {
          stream.getTracks().forEach(t => t.stop());
          return;
        }
        streamRef.current = stream;

        const devices = await BrowserMultiFormatReader.listVideoInputDevices();

        if (!cancelled && devices.length > 1) {
          const backCamera = devices.find(d =>
            d.label.toLowerCase().includes("back") ||
            d.label.toLowerCase().includes("rear") ||
            d.label.toLowerCase().includes("environment")
          );
          if (backCamera && backCamera.deviceId) {
            const currentTrackSettings = stream.getVideoTracks()[0]?.getSettings();
            if (currentTrackSettings?.deviceId !== backCamera.deviceId) {
              stream.getTracks().forEach(t => t.stop());
              const betterStream = await navigator.mediaDevices.getUserMedia({
                video: {
                  deviceId: { exact: backCamera.deviceId },
                  width: { ideal: 1280 },
                  height: { ideal: 720 },
                },
              });
              if (cancelled) {
                betterStream.getTracks().forEach(t => t.stop());
                return;
              }
              streamRef.current = betterStream;
            }
          }
        }

        const activeStream = streamRef.current;
        if (!activeStream) return;

        const track = activeStream.getVideoTracks()[0];
        if (track) {
          try {
            const capabilities = track.getCapabilities?.() as MediaTrackCapabilitiesWithTorch | undefined;
            if (capabilities?.torch) {
              setTorchAvailable(true);
            }
          } catch {
          }
        }

        if (videoRef.current) {
          videoRef.current.srcObject = activeStream;
          await videoRef.current.play();
        }

        const controls = await reader.decodeFromStream(activeStream, videoRef.current!, (result) => {
          if (cancelled) return;
          if (result) {
            const text = result.getText();
            if (text && text.length > 0) {
              resolve(text);
            }
          }
        });
        if (cancelled) {
          controls.stop();
        } else {
          controlsRef.current = controls;
        }
      } catch (err: unknown) {
        if (cancelled) return;
        const errorObj = err instanceof Error ? err : null;
        const errorName = errorObj?.name ?? "";
        if (errorName === "NotAllowedError") {
          setError("Camera permission denied. Please allow camera access in your browser settings and try again.");
        } else if (errorName === "NotFoundError" || errorName === "OverconstrainedError") {
          setError("No camera found on this device.");
        } else {
          setError("Could not start the camera. Please try again.");
        }
      }
    };

    startScanning();

    return () => {
      cancelled = true;
      if (controlsRef.current) {
        controlsRef.current.stop();
        controlsRef.current = null;
      }
      readerRef.current = null;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
    };
  }, [isOpen, resolve]);

  const toggleTorch = async () => {
    if (!streamRef.current) return;
    const track = streamRef.current.getVideoTracks()[0];
    if (!track) return;
    try {
      const newState = !torchOn;
      const advancedConstraint: MediaTrackConstraintSetWithTorch = { torch: newState };
      await track.applyConstraints({ advanced: [advancedConstraint] });
      setTorchOn(newState);
    } catch {
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/90 flex flex-col items-center justify-center p-4"
      style={containment.overlayStyle}
      onClick={(e) => { if (e.target === e.currentTarget) closeScanner(); }}
      onKeyDown={focusTrap.onKeyDown}
      role="presentation"
    >
      <div
        ref={panelRef}
        className="relative max-h-full w-full max-w-lg overflow-y-auto mx-auto flex flex-col items-center"
        role="dialog"
        aria-modal="true"
        aria-label="Barcode scanner"
        tabIndex={-1}
      >
        <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
          {torchAvailable && (
            <button
              onClick={toggleTorch}
              className="w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-black/70 transition-colors"
            >
              {torchOn ? <ZapOff className="w-5 h-5" /> : <Zap className="w-5 h-5" />}
            </button>
          )}
          <button
            onClick={closeScanner}
            className="w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-black/70 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="relative w-full aspect-[4/3] max-h-[70vh] rounded-xl overflow-hidden border border-white/10">
          <video
            ref={videoRef}
            className="w-full h-full object-cover"
            playsInline
            muted
            autoPlay
          />

          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute inset-[15%] border-2 border-white/30 rounded-lg">
              <div className="absolute -top-[1px] -left-[1px] w-8 h-8 border-t-2 border-l-2 border-primary rounded-tl-lg" />
              <div className="absolute -top-[1px] -right-[1px] w-8 h-8 border-t-2 border-r-2 border-primary rounded-tr-lg" />
              <div className="absolute -bottom-[1px] -left-[1px] w-8 h-8 border-b-2 border-l-2 border-primary rounded-bl-lg" />
              <div className="absolute -bottom-[1px] -right-[1px] w-8 h-8 border-b-2 border-r-2 border-primary rounded-br-lg" />
            </div>

            <div className="absolute left-[15%] right-[15%] top-1/2 h-[2px] -translate-y-1/2">
              <div className="w-full h-full bg-gradient-to-r from-transparent via-primary to-transparent animate-pulse opacity-60" />
            </div>
          </div>

          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/80 px-6">
              <div className="text-center space-y-3">
                <Camera className="w-10 h-10 text-muted-foreground mx-auto" />
                <p className="text-sm text-white/80">{error}</p>
                <button
                  onClick={closeScanner}
                  className="text-sm text-primary underline hover:text-primary/80"
                >
                  Close
                </button>
              </div>
            </div>
          )}

          {!error && (
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-4 py-3">
              <div className="flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-primary" />
                <span className="text-sm text-white/80 font-medium">Point camera at barcode</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
